import React, { useCallback, useContext, useMemo, useState } from 'react';
import PageLayout from '../pagelayout';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import translation from './praksissteder.student.i18n';
import { dateWithLocale } from '../date.utils';
import * as css from './praksissteder.student.module.scss';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import {
  PraksisaktivitetDto,
  PraksisstedAvdelingDto,
} from '../api/generated/student-generated-api';
import { StudentApiContext } from '../api/StudentApiContext';
import { Grid } from '@mui/material';
import { BackButton } from '../pagelayout/backButton';
import Icon, { IconType } from '../components/icon';
import Button from '../components/button';
import { LanguageContext } from '../i18n/LanguageStore';
import Checkbox from '../components/input/checkbox';
import wishListModalImg from 'url:./IllustrasjonOnskeliste.svg';
import classNames from 'classnames';
import {
  mapAddressToJSX,
  mapHierarchyToHeadings,
  praksisstedProfilToAddress,
} from '../components/hierarchy/onhoverhierarchyinfo';
import {
  CustomizableDialogContainer,
  CustomizableDialogContent,
  CustomizableDialogFooter,
  CustomizableDialogHeader,
  CustomizeableDialogTitle,
} from '../components/dialog';
import { useFormatMessageFor } from '../i18n/translation';
import useElementRefCallbackOn from '../hooks/useElementRefCallbackOn';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';

const useLoadData = () => {
  const { praksisApi, praksisstedApi } = useContext(StudentApiContext);
  const { id } = useParams<{ id: string }>();
  const {
    data: praksisaktivitet,
    loading: loading1,
    error: error1,
  } = useLoader(
    () =>
      praksisApi.praksisaktivitetIdGet({
        pathParams: { id: id! },
      }),
    [],
    true,
    true,
  );

  const {
    data: praksissteder,
    loading: loading2,
    error: error2,
  } = useLoader(
    () =>
      praksisstedApi.praksisstederForOnskelistePraksisaktivitetIdGet({
        pathParams: { praksisaktivitetId: id! },
      }),
    [],
    true,
    true,
  );
  return {
    praksisaktivitet,
    praksissteder,
    loading: loading1 || loading2,
    error: error1 || error2,
  };
};

const sortWishlistAndSteder = (
  praksissteder: PraksisstedAvdelingDto[],
  onskeliste: Onske[],
) => {
  if (onskeliste.length === 0) return praksissteder;

  const onskeListeToPraksissteder = onskeliste.reduce(
    (acc: PraksisstedAvdelingDto[], onske: Onske) => {
      const praksissted = praksissteder.find(
        (sted) => sted.id === onske.praksisplassId,
      );

      return praksissted ? acc.concat(praksissted) : acc;
    },
    [],
  );

  return onskeListeToPraksissteder.concat(
    praksissteder.filter(
      (praksissted) => !onskeListeToPraksissteder.includes(praksissted),
    ),
  );
};

const getWishlistInfoModalShownFromSessionStorage = (): boolean => {
  const value = sessionStorage.getItem('wishlistInfoModalShown');

  return value ? JSON.parse(value) : false;
};

const putWishlistInfoModalShownInSessionStorage = () => {
  sessionStorage.setItem('wishlistInfoModalShown', JSON.stringify(true));
};

const PraksisstederPage = () => {
  const { praksisaktivitet, praksissteder, loading } = useLoadData();

  if (!praksisaktivitet || !praksissteder || loading) {
    return <Spinner />;
  }
  return (
    <Praksissteder
      praksisaktivitet={praksisaktivitet}
      praksissteder={praksissteder}
    />
  );
};

interface Props {
  praksisaktivitet: PraksisaktivitetDto;
  praksissteder: PraksisstedAvdelingDto[];
}

interface Onske {
  id: string;
  praksisplassId: string;
}
const Praksissteder = ({ praksisaktivitet, praksissteder }: Props) => {
  const { selectedLanguage } = useContext(LanguageContext);
  const { formatMessage } = useIntl();
  const { praksisApi } = useContext(StudentApiContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();
  const location = useLocation();

  const [modalIsOpen, setModalIsOpen] = useState(
    !getWishlistInfoModalShownFromSessionStorage(),
  );
  const [hidePraksissted, setHidePraksissted] = useState(false);
  const [hideOnskeliste, setHideOnskeliste] = useState(false);
  const [onskeliste, setOnskeliste] = useState<Onske[]>(
    praksisaktivitet.onskeliste.map((onske) => {
      return {
        id: onske.id || '',
        praksisplassId: onske.praksisplass.id,
      };
    }),
  );

  const { startDato: start, sluttDato: slutt, emne } = praksisaktivitet;

  const startDato = new Date(start);
  const sluttDato = new Date(slutt);
  const maxNumOnsker =
    praksissteder.length < praksisaktivitet.maxAntallOnsker
      ? praksissteder.length
      : praksisaktivitet.maxAntallOnsker;

  const labels = useFormatMessageFor({
    ...translation,
    title: {
      ...translation.title,
      values: { emne: `${emne.kode} - ${emne.navn}` },
    },
    choosePraksissteder: {
      ...translation.choosePraksissteder,
      values: { maxNum: maxNumOnsker.toString() },
    },
  });

  const backButton = (
    <BackButton
      title={labels.cancel}
      path={location.pathname.replace('/praksissteder', '')}
    />
  );

  const handleSetOnskelisteChecked = (checked: boolean) => {
    if (hidePraksissted) {
      setHidePraksissted(false);
    }
    setHideOnskeliste(checked);
  };

  const handleSetPraksisstederChecked = (checked: boolean) => {
    if (hideOnskeliste) {
      setHideOnskeliste(false);
    }
    setHidePraksissted(checked);
  };

  const addOnske = useCallback(
    (praksisplassId: string) => {
      praksisApi
        .praksisaktivitetIdOnskePost({
          pathParams: { id: praksisaktivitet.id },
          body: praksisplassId,
        })
        .then((onskeId) => {
          setOnskeliste([...onskeliste, { id: onskeId, praksisplassId }]);
        })
        .catch(errorNotificationCallback);
    },
    [errorNotificationCallback, onskeliste, praksisApi, praksisaktivitet.id],
  );

  const getOnskeIdByPraksisstedId = useCallback(
    (praksisstedId: string): string => {
      const onske = onskeliste.find(
        (onske) => onske.praksisplassId === praksisstedId,
      );
      return onske?.id || '';
    },
    [onskeliste],
  );

  const deleteOnske = useCallback(
    (praksisplassId: string) => {
      praksisApi
        .praksisaktivitetIdOnskeOnskeIdDelete({
          pathParams: {
            id: praksisaktivitet.id,
            onskeId: getOnskeIdByPraksisstedId(praksisplassId),
          },
        })
        .then(() => {
          const idx = onskeliste
            .map((onske) => onske.praksisplassId)
            .indexOf(praksisplassId);
          onskeliste.splice(idx, 1);
          setOnskeliste([...onskeliste]);
        })
        .catch(errorNotificationCallback);
    },
    [
      errorNotificationCallback,
      getOnskeIdByPraksisstedId,
      onskeliste,
      praksisaktivitet,
      praksisApi,
    ],
  );

  const toggleChosen = useCallback(
    (praksisstedId?: string) => {
      if (!praksisstedId) {
        return;
      }
      if (onskeliste.some((onske) => onske.praksisplassId === praksisstedId)) {
        deleteOnske(praksisstedId);
      } else {
        addOnske(praksisstedId);
      }
    },
    [addOnske, deleteOnske, onskeliste],
  );

  const sortedPraksissteder = useMemo(
    () => sortWishlistAndSteder(praksissteder, onskeliste),
    [onskeliste, praksissteder],
  );

  const praksisstederToRender = useMemo(
    () =>
      sortedPraksissteder.map((sted, idx) => {
        const onskelisteToPraksisstedIds = onskeliste.map(
          (onske) => onske.praksisplassId,
        );

        if (
          (hidePraksissted && hideOnskeliste) ||
          (!hidePraksissted &&
            hideOnskeliste &&
            onskelisteToPraksisstedIds.includes(sted.id!)) ||
          (!hideOnskeliste &&
            hidePraksissted &&
            !onskelisteToPraksisstedIds.includes(sted.id!))
        ) {
          return <React.Fragment key={sted.id} />;
        }

        return (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            xl={3}
            key={sted.id}
            className={css.praksisstedWrapper}
          >
            <Praksissted
              sted={sted}
              formatMessage={formatMessage}
              chosen={onskeliste.some(
                (onske) => onske.praksisplassId === sted.id,
              )}
              toggleChosen={toggleChosen}
              maxNumOnsker={maxNumOnsker}
              onskeliste={onskeliste}
              stedPriority={idx + 1}
            />
          </Grid>
        );
      }),
    [
      onskeliste,
      formatMessage,
      toggleChosen,
      maxNumOnsker,
      sortedPraksissteder,
      hideOnskeliste,
      hidePraksissted,
    ],
  );

  const helpBtnRef = useElementRefCallbackOn<HTMLButtonElement>(
    !modalIsOpen,
    (element) => element.focus(),
    false,
  );

  const handleModalIsOpen = (isOpen: boolean = false) => {
    setModalIsOpen(isOpen);

    if (!isOpen) {
      putWishlistInfoModalShownInSessionStorage();
    }
  };

  return (
    <PageLayout
      isIndented
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={backButton}
    >
      <Grid container className={css.generalInfo}>
        <Grid item xs={12} sm={8}>
          <div
            className={classNames(
              css.numChosen,
              `numChosen_${onskeliste.length}`,
            )}
          >
            {onskeliste.length === 0 && labels.noChosenPraksissteder}
            {onskeliste.length > 0 &&
              formatMessage(
                {
                  id: translation.numChosenPraksissteder.id,
                },
                {
                  num: onskeliste.length,
                  maxNum: maxNumOnsker,
                },
              )}
          </div>
        </Grid>
        <Grid item xs={12} sm={4} className={css.datePeriod}>
          <p>{labels.praksisperiode}</p>
          <div className={css.dates}>
            {dateWithLocale(startDato, selectedLanguage)} -{' '}
            {dateWithLocale(sluttDato, selectedLanguage)}
          </div>
        </Grid>
        <Grid item xs={6}>
          <h2 className={css.choosePraksissteder}>
            {labels.choosePraksissteder}
            <button
              className={css.helpIcon}
              onClick={() => handleModalIsOpen(true)}
              title={labels.helpBtn}
              ref={helpBtnRef}
            >
              <Icon type={IconType.Help} title="" />
            </button>
          </h2>
        </Grid>
        <Grid item xs={6} className={css.flex}>
          <Checkboxes
            hidePraksissted={hidePraksissted}
            handleSetPraksisstederChecked={handleSetPraksisstederChecked}
            hideOnskeliste={hideOnskeliste}
            handleSetOnskelisteChecked={handleSetOnskelisteChecked}
          />
        </Grid>
      </Grid>
      <Grid container spacing={5} className={css.praksisstederWrapper}>
        {praksisstederToRender}
      </Grid>
      {modalIsOpen && (
        <WishListInfoModal modalClose={() => handleModalIsOpen(false)} />
      )}
    </PageLayout>
  );
};

interface CheckboxesProps {
  hidePraksissted: boolean;
  handleSetPraksisstederChecked: (checked: boolean) => void;
  hideOnskeliste: boolean;
  handleSetOnskelisteChecked: (checked: boolean) => void;
}

const Checkboxes = ({
  hidePraksissted,
  hideOnskeliste,
  handleSetOnskelisteChecked,
  handleSetPraksisstederChecked,
}: CheckboxesProps) => {
  const labels = useFormatMessageFor(translation);

  return (
    <div className={css.checkboxes}>
      <Checkbox
        id={'skjulPraksissteder'}
        checked={hidePraksissted}
        setChecked={handleSetPraksisstederChecked}
        label={labels.skjulPraksissteder}
      />
      <Checkbox
        id={'skjulOnskeliste'}
        checked={hideOnskeliste}
        setChecked={handleSetOnskelisteChecked}
        label={labels.skjulOnskeliste}
      />
    </div>
  );
};

interface PraksisstedProps {
  sted: PraksisstedAvdelingDto;
  chosen: boolean;
  formatMessage: Function;
  toggleChosen: (id?: string) => void;
  maxNumOnsker: number;
  onskeliste: Onske[];
  stedPriority: number;
}

const Praksissted = ({
  sted,
  chosen,
  toggleChosen,
  formatMessage,
  maxNumOnsker,
  onskeliste,
  stedPriority,
}: PraksisstedProps) => {
  const labels = {
    readMore: formatMessage({ id: translation.readMore.id }),
    choose: formatMessage({ id: translation.choose.id }),
    unchoose: formatMessage({ id: translation.unchoose.id }),
    prioritering: formatMessage({ id: translation.prioritering.id }),
  };

  const { praksisstedApi } = useContext(StudentApiContext);
  const { data } = useLoader(() =>
    praksisstedApi.praksisstedprofilIdGet({
      pathParams: { id: sted.id! },
    }),
  );

  const ancestors = data?.noder?.over.slice(0) || [];

  return (
    <div className={css.praksissted}>
      {sted.bildeUrler && sted.bildeUrler.length > 0 && (
        <div className={css.picture}>
          <img src={sted.bildeUrler[0]} alt="" />
        </div>
      )}
      {!sted.bildeUrler ||
        (sted.bildeUrler.length === 0 && (
          <div className={css.replacementPicture}>
            <div className={css.iconWrapper}>
              <Icon type={IconType.EndNode} title={''} htmlId={sted.id} />
            </div>
          </div>
        ))}
      <div className={css.text}>
        <h3 className={css.avdelingsnavn}>{sted.navn}</h3>
        <div className={classNames(css.headings, `praksisstedTopp ${sted.id}`)}>
          {mapHierarchyToHeadings(
            (ancestors.reverse() || []).concat({
              id: data?.id || '',
              navn: data?.navn || '',
            }),
          )}
        </div>
        <div className={css.beskrivelse}>
          {mapAddressToJSX(praksisstedProfilToAddress(data))}
        </div>
        <div className={css.readMoreLink}>
          <NavLink to={`/practiceplaces/profile/${sted.id}`}>
            {labels.readMore}
          </NavLink>
        </div>
      </div>
      <div className={css.choiceAndPriority}>
        {chosen && (
          <p
            className={css.priorityWrapper}
            data-cy={'prioritering'}
          >{`${labels.prioritering} ${stedPriority} / ${onskeliste.length}`}</p>
        )}
        <Button.Secondary
          onClick={() => toggleChosen(sted.id)}
          disabled={!chosen && onskeliste.length >= maxNumOnsker}
          className={classNames(css.choiceBtn, chosen && css.unchooseButton)}
        >
          {chosen ? labels.unchoose : labels.choose}
        </Button.Secondary>
      </div>
    </div>
  );
};

interface WishListInfoModalProps {
  modalClose: () => void;
}

const WishListInfoModal = ({ modalClose }: WishListInfoModalProps) => {
  const { formatMessage } = useIntl();
  const labels = {
    wishListInfoHeader: formatMessage({
      id: translation.wishListInfoHeader.id,
    }),
    wishListInfo1: formatMessage({ id: translation.wishListInfo1.id }),
    wishListInfo2: formatMessage({ id: translation.wishListInfo2.id }),
    wishListInfo3: formatMessage({ id: translation.wishListInfo3.id }),
    dontShowAgain: formatMessage({ id: translation.dontShowAgain.id }),
    understood: formatMessage({ id: translation.understood.id }),
  };

  return (
    <CustomizableDialogContainer
      className={css.dialog}
      onCancel={modalClose}
      isOpen
      dataCy={'wishlist-info-modal'}
    >
      <CustomizableDialogHeader className={css.dialogHeader}>
        <img src={wishListModalImg} alt="" />
        <CustomizeableDialogTitle>
          {labels.wishListInfoHeader}
        </CustomizeableDialogTitle>
      </CustomizableDialogHeader>
      <CustomizableDialogContent className={css.content}>
        <p>{labels.wishListInfo1}</p>
        <p>{labels.wishListInfo2}</p>
        <p>{labels.wishListInfo3}</p>
      </CustomizableDialogContent>
      <CustomizableDialogFooter className={css.footer}>
        <Button.Secondary
          onClick={modalClose}
          testSelector={'wishlist-info-modal-close'}
        >
          {labels.understood}
        </Button.Secondary>
      </CustomizableDialogFooter>
    </CustomizableDialogContainer>
  );
};

export default PraksisstederPage;
